import React, { useEffect, useState } from 'react';
import { authAPI } from '../api';

/**
 * StorageWarningBanner — calm, editorial banner that shows up only when the
 * workspace is approaching or has reached its storage limit.
 *
 * Levels (driven entirely by /api/auth/usage):
 *   - 'ok'       → render nothing
 *   - 'warn'     → soft notice (≥ 80%) — amber tone
 *   - 'critical' → stronger notice (≥ 90%) — deeper amber
 *   - 'blocked'  → at/over 100% — soft red, explains uploads are paused
 *
 * Reads the language from localStorage (same convention as other Maestra pages).
 *
 * Props:
 *   - usage:    optional pre-fetched usage payload. If omitted, the component
 *               fetches /auth/usage itself.
 *   - compact:  smaller padding, used inside drawers / settings columns.
 *   - onDismiss: optional handler to allow the user to close the banner for
 *                this session (banner re-appears on reload).
 */
const COPY = {
  en: {
    warn_title:    "You're approaching your storage limit.",
    warn_body:     "You've used {used} of {limit} ({pct}%). Consider deleting unused images or asking your studio admin to raise your limit.",
    critical_title:"Storage almost full.",
    critical_body: "You've used {used} of {limit} ({pct}%). New uploads will be blocked once you reach 100%.",
    blocked_title: "Storage limit reached.",
    blocked_body:  "You've used {used} of {limit}. Uploads are paused — viewing, deleting and exports remain available. Free up space or contact your admin.",
    dismiss:       "Dismiss",
  },
  pl: {
    warn_title:    "Zbliżasz się do limitu przestrzeni.",
    warn_body:     "Wykorzystano {used} z {limit} ({pct}%). Rozważ usunięcie nieużywanych zdjęć lub poproś administratora o zwiększenie limitu.",
    critical_title:"Przestrzeń prawie pełna.",
    critical_body: "Wykorzystano {used} z {limit} ({pct}%). Nowe przesyłania zostaną zablokowane po osiągnięciu 100%.",
    blocked_title: "Limit przestrzeni osiągnięty.",
    blocked_body:  "Wykorzystano {used} z {limit}. Przesyłanie jest wstrzymane — podgląd, usuwanie i eksport pozostają dostępne. Zwolnij miejsce lub skontaktuj się z administratorem.",
    dismiss:       "Zamknij",
  },
  de: {
    warn_title:    "Sie nähern sich Ihrem Speicherlimit.",
    warn_body:     "Sie haben {used} von {limit} verwendet ({pct}%). Erwägen Sie, ungenutzte Bilder zu löschen oder Ihren Administrator zu bitten, das Limit zu erhöhen.",
    critical_title:"Speicher fast voll.",
    critical_body: "Sie haben {used} von {limit} verwendet ({pct}%). Neue Uploads werden blockiert, sobald 100% erreicht sind.",
    blocked_title: "Speicherlimit erreicht.",
    blocked_body:  "Sie haben {used} von {limit} verwendet. Uploads sind pausiert — Ansicht, Löschen und Export bleiben verfügbar. Geben Sie Speicher frei oder kontaktieren Sie Ihren Administrator.",
    dismiss:       "Schließen",
  },
};

const TONES = {
  warn:     { bg: '#fbf5e8', border: '#e5c97a', text: '#6b5520' },
  critical: { bg: '#f9ecd6', border: '#cf9a3d', text: '#5e3f10' },
  blocked:  { bg: '#f5e3df', border: '#b35a4a', text: '#6b2d22' },
};

function formatMB(mb) {
  if (mb == null) return '—';
  if (mb < 1) return `${Math.round(mb * 1024)} KB`;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

function fillTemplate(str, vars) {
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : ''));
}

const StorageWarningBanner = ({ usage: usageProp, compact = false, onDismiss }) => {
  const [usage, setUsage] = useState(usageProp || null);
  const [hidden, setHidden] = useState(false);
  const lang = (typeof window !== 'undefined' && localStorage.getItem('language')) || 'en';
  const t = COPY[lang] || COPY.en;

  useEffect(() => {
    if (usageProp) { setUsage(usageProp); return; }
    let alive = true;
    authAPI.getUsage()
      .then((res) => { if (alive) setUsage(res.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [usageProp]);

  if (hidden || !usage) return null;
  const level = usage.storage_level || 'ok';
  if (level === 'ok') return null;

  const tone = TONES[level] || TONES.warn;
  const used  = formatMB(usage.storage_used_mb);
  const limit = formatMB(usage.storage_limit_mb);
  const pct   = Math.min(100, Math.round(usage.storage_percent || 0));

  const titleKey = `${level}_title`;
  const bodyKey  = `${level}_body`;
  const body = fillTemplate(t[bodyKey], { used, limit, pct });

  return (
    <div
      data-testid={`storage-warning-${level}`}
      role="status"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 18,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.text,
        padding: compact ? '12px 16px' : '16px 22px',
        margin: compact ? '0 0 14px' : '0 0 22px',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontFamily: 'Playfair Display, serif',
            fontSize: compact ? 13 : 14,
            letterSpacing: '0.01em',
            fontWeight: 500,
          }}
        >
          {t[titleKey]}
        </p>
        <p
          style={{
            margin: '4px 0 0',
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: compact ? 13 : 14,
            lineHeight: 1.55,
            color: tone.text,
            opacity: 0.9,
          }}
        >
          {body}
        </p>
      </div>
      {onDismiss !== false && (
        <button
          type="button"
          data-testid="storage-warning-dismiss"
          onClick={() => { setHidden(true); onDismiss && onDismiss(); }}
          style={{
            background: 'transparent',
            border: 'none',
            color: tone.text,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            padding: '2px 4px',
            opacity: 0.7,
          }}
        >
          {t.dismiss}
        </button>
      )}
    </div>
  );
};

export default StorageWarningBanner;
